import { useMemo } from "react";

import {
  useCurrentLeagueContext,
  useCurrentOrganizationContext,
  useCurrentPlatformContext,
} from "@/contexts";
import { CapabilitySet, ScopeLevel } from "@/types/capability.types";

import { useLeagueCapabilities } from "./useLeagueCapabilities";
import { useOrganizationCapabilities } from "./useOrganizationCapabilities";
import { usePlatformCapabilities } from "./usePlatformCapabilities";

/**
 * The capability set of the most specific active context: league when a league
 * is selected, else the current organization, else the platform (platform-only
 * mode). This is what list pages and permission hooks check — the deciding
 * grant for row-invariant surfaces lives on the active scope's node, and the
 * resolver's ancestor walk already folds ancestor grants into descendant sets.
 *
 * Platform-only exception (single, scoped OR-bridge): the platform-only active
 * scope is the union of the PLATFORM-node caps and the platform's `dataOwner`
 * federation caps. Creates in platform-only mode attribute to the dataOwner
 * federation (`useCreateOwnerOrganization`), so the effective scope is
 * `platform ∪ dataOwner federation`. The league / organization branches are
 * unchanged. Widening the create affordance is safe: the BE list-scope still
 * enforces per-row access.
 */
export const useActiveScopeCapabilities = (): {
  level: ScopeLevel;
  capabilities: CapabilitySet;
} => {
  const { platform } = useCurrentPlatformContext();
  const { league } = useCurrentLeagueContext();
  const { organization } = useCurrentOrganizationContext();
  const platformCapabilities = usePlatformCapabilities();
  const organizationCapabilities = useOrganizationCapabilities();
  const leagueCapabilities = useLeagueCapabilities();

  const dataOwnerCapabilities = platform?.dataOwner?.capabilities;
  const platformOnlyCapabilities = useMemo<CapabilitySet>(
    () => new Set<string>([...platformCapabilities, ...(dataOwnerCapabilities ?? [])]),
    [platformCapabilities, dataOwnerCapabilities]
  );

  if (league !== null) {
    return { level: "league", capabilities: leagueCapabilities };
  }

  if (organization !== null) {
    return { level: "organization", capabilities: organizationCapabilities };
  }

  return { level: "platform", capabilities: platformOnlyCapabilities };
};
